import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@veritrellis/ui";
import type {
  ConnectorCatalogEntry,
  ConnectorPlatform,
  InboundAuthType,
  OutboundAuthType
} from "@veritrellis/shared/connectors";

export type ConnectorSetupDraft = {
  environment: "sandbox" | "production";
  label: string;
  outbound_base_url: string;
  inbound_auth_type: InboundAuthType;
  outbound_auth_type: OutboundAuthType;
  domainAllowlistInput: string;
};

export const buildInitialSetupDraft = (input: {
  platform: ConnectorPlatform;
  entry: ConnectorCatalogEntry;
  defaultEnvironment: "sandbox" | "production";
}): ConnectorSetupDraft => {
  const { defaultConfig } = input.entry;
  return {
    environment: input.defaultEnvironment,
    label: defaultConfig.label,
    outbound_base_url: defaultConfig.outbound_base_url,
    inbound_auth_type: defaultConfig.inbound_auth_type,
    outbound_auth_type: defaultConfig.outbound_auth_type,
    domainAllowlistInput: defaultConfig.domain_allowlist.join(", ")
  };
};

export const parseDomainAllowlist = (raw: string): string[] => {
  return raw
    .split(/[,;\n]/u)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
};

export const ConnectorSetupForm = (input: {
  entry: ConnectorCatalogEntry;
  draft: ConnectorSetupDraft;
  onDraftChange: (next: ConnectorSetupDraft) => void;
  allowProduction: boolean;
}) => {
  const inboundOptions = input.entry.supports.inboundAuthTypes.map((v) => ({ value: v, label: v }));
  const outboundOptions = input.entry.supports.outboundAuthTypes.map((v) => ({
    value: v,
    label: v
  }));

  const envOptions = [
    { value: "sandbox", label: "Sandbox" },
    ...(input.allowProduction ? [{ value: "production" as const, label: "Production" }] : [])
  ];

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Installation for <strong>{input.entry.displayName}</strong>. Invoke URL is issued after
        save.
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label>Environment</Label>
          <p className="text-xs text-muted-foreground">
            {input.allowProduction
              ? "Production changes may require admin when strict separation is on."
              : "Your role cannot create or edit production connectors in this workspace."}
          </p>
          <Select
            value={input.draft.environment}
            onValueChange={(v) => {
              if (v === "sandbox" || v === "production") {
                input.onDraftChange({ ...input.draft, environment: v });
              }
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {envOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Label</Label>
          <p className="text-xs text-muted-foreground">Shown in the list of installations.</p>
          <Input
            value={input.draft.label}
            onChange={(e) => {
              input.onDraftChange({ ...input.draft, label: e.currentTarget.value });
            }}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>Outbound base URL</Label>
        <p className="text-xs text-muted-foreground">
          HTTPS origin your relay handler is reachable at.
        </p>
        <Input
          value={input.draft.outbound_base_url}
          onChange={(e) => {
            input.onDraftChange({ ...input.draft, outbound_base_url: e.currentTarget.value });
          }}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label>Inbound auth (Veritrellis)</Label>
          <Select
            value={input.draft.inbound_auth_type}
            onValueChange={(v) => {
              if (inboundOptions.some((o) => o.value === v)) {
                input.onDraftChange({ ...input.draft, inbound_auth_type: v as InboundAuthType });
              }
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {inboundOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Outbound auth (relay)</Label>
          <Select
            value={input.draft.outbound_auth_type}
            onValueChange={(v) => {
              if (outboundOptions.some((o) => o.value === v)) {
                input.onDraftChange({ ...input.draft, outbound_auth_type: v as OutboundAuthType });
              }
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {outboundOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>Domain allowlist (optional)</Label>
        <p className="text-xs text-muted-foreground">
          Extra outbound hosts allowed beyond the installation base URL host. Comma-separated.
        </p>
        <Input
          value={input.draft.domainAllowlistInput}
          onChange={(e) => {
            input.onDraftChange({ ...input.draft, domainAllowlistInput: e.currentTarget.value });
          }}
        />
      </div>
    </div>
  );
};
